// pendingHandoff — pass an in-flight Promise from one route to the next.
//
// react-router's navigate(path, { state }) stores `state` via
// history.pushState, which requires the value to be STRUCTURED-CLONEABLE.
// A Promise is not: passing one directly throws
// "Failed to execute 'pushState' on 'History': #<Promise> could not be
// cloned" — silently killing the navigation itself, not just the feature that
// tried to carry the promise.
//
// The fix is a token indirection: stash the promise in an in-memory map and
// hand nav state a plain string key instead. Both routes live in the same SPA
// runtime, so the map survives the navigation; a hard reload or a link opened
// in a new tab naturally has no entry, which callers must treat as "nothing to
// wait for" (never as an error).
const pending = new Map();
let counter = 0;

/**
 * Stash a promise and return a token to carry through navigate(path, { state }).
 * @param {Promise<any>} promise
 * @returns {string} token
 */
export function stashPendingPromise(promise) {
  const token = `ph_${Date.now()}_${(counter += 1)}`;
  pending.set(token, promise);
  // Self-cleaning: if the destination never mounts to consume it (the user
  // cancels, or navigates elsewhere first), don't hold the promise forever.
  promise.finally(() => {
    setTimeout(() => pending.delete(token), 0);
  }).catch(() => {}); // already-handled elsewhere; this is only cleanup
  return token;
}

/**
 * Retrieve and remove a stashed promise by token. Returns undefined for an
 * unknown/already-consumed token — callers must treat that as "nothing to
 * wait for", not as an error (e.g. the page was reloaded, or opened directly).
 * @param {string | undefined} token
 * @returns {Promise<any> | undefined}
 */
export function takePendingPromise(token) {
  if (!token) return undefined;
  const promise = pending.get(token);
  pending.delete(token);
  return promise;
}
